import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from "@pax/ui";
import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { api, orpc, reachable, requestErrorMessage } from "@/lib/api";
import type { AutoTopUpDraft } from "../format";
import { formatMoney, type PackOffer, thresholdCredits } from "../format";
import { refreshCredits, topUpSettingsQuery, useUnderThreshold } from "../queries";
import { signalAutoTopUpSuccess } from "../resume-after-checkout";

/**
 * Confirms turning automatic top-up on when the saved payment method already allows off-session
 * charges. A player already under the threshold is charged as it turns on.
 */
export function ConfirmAutoTopUpDialog({
  draft,
  pack,
  paymentMethodLabel: methodLabel,
  savingEdits,
  onClose,
  onUseDifferentPaymentMethod,
}: {
  draft: AutoTopUpDraft | null;
  pack: PackOffer | null;
  paymentMethodLabel: string;
  savingEdits: boolean;
  onClose: () => void;
  /** Opens Stripe setup for the same rule. Called inside the click, so its tab is allowed. */
  onUseDifferentPaymentMethod: () => void;
}) {
  const queryClient = useQueryClient();
  const threshold = draft ? thresholdCredits(draft.autoThresholdRaw) : "";
  const buysNow = useUnderThreshold(draft?.autoThresholdRaw ?? null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const quoteQuery = useQuery({
    ...orpc.payments.savedPaymentMethod.quote.queryOptions({
      input: draft ? { body: { offerId: draft.autoOfferId } } : skipToken,
    }),
    enabled: pack !== null,
    staleTime: 0,
    retry: false,
  });
  const quoted = quoteQuery.data?.status === "quoted" ? quoteQuery.data : null;
  const totalLabel =
    quoted !== null
      ? formatMoney(quoted.totalCents)
      : pack
        ? `${formatMoney(pack.priceCents)} plus tax`
        : "";

  const save = useMutation({
    mutationFn: async () => {
      if (!draft) throw new Error("Missing automatic top-up rule.");
      return reachable(api.payments.autoTopUp.set({ body: draft }));
    },
    onSuccess: updated => {
      queryClient.setQueryData(topUpSettingsQuery.queryKey, updated);
      void refreshCredits(queryClient);
      // A first charge that failed pauses the rule, and the dialog shows why instead.
      if (updated.autoState === "on" || updated.autoState === "charging") signalAutoTopUpSuccess();
      onClose();
    },
  });

  return (
    <Dialog open={draft !== null && pack !== null} onOpenChange={open => !open && onClose()}>
      <DialogContent nestedBackdrop initialFocus={cancelButton}>
        {draft && pack && (
          <>
            <DialogHeader>
              <DialogTitle>
                {savingEdits ? "Save automatic top-up?" : "Turn on automatic top-up?"}
              </DialogTitle>
              <DialogDescription>
                {buysNow
                  ? `You have under ${threshold} credits, so Pax charges ${methodLabel} ${totalLabel} for ${pack.name} right away, and again whenever your credits drop below ${threshold}.`
                  : `When your credits drop below ${threshold}, Pax charges ${methodLabel} ${totalLabel} for ${pack.name}. Nothing is charged now.`}
              </DialogDescription>
            </DialogHeader>

            {quoteQuery.isPending ? (
              <Skeleton className="h-16 rounded-lg" />
            ) : quoteQuery.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {requestErrorMessage(quoteQuery.error)}
              </p>
            ) : quoted ? (
              <dl className="grid grid-cols-[1fr_auto] gap-y-1 text-sm tabular-nums">
                <dt className="text-muted-foreground">Price when it runs</dt>
                <dd className="text-right">{formatMoney(quoted.subtotalCents)}</dd>
                <dt className="text-muted-foreground">Tax</dt>
                <dd className="text-right">{formatMoney(quoted.taxCents)}</dd>
                <dt className="font-medium">Total</dt>
                <dd className="text-right font-medium">{formatMoney(quoted.totalCents)}</dd>
              </dl>
            ) : null}

            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto justify-self-start p-0 text-muted-foreground hover:text-foreground"
              disabled={save.isPending}
              onClick={onUseDifferentPaymentMethod}
            >
              Use a different payment method
            </Button>

            {save.error && (
              <p
                role="alert"
                className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {requestErrorMessage(save.error)}
              </p>
            )}

            <DialogFooter className="flex-row flex-wrap justify-end">
              <Button
                ref={cancelButton}
                type="button"
                variant="outline"
                disabled={save.isPending}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={save.isPending || quoteQuery.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending
                  ? buysNow
                    ? "Buying…"
                    : savingEdits
                      ? "Saving…"
                      : "Turning on…"
                  : savingEdits
                    ? "Save"
                    : "Turn on auto top-up"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
